import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { WorkspaceStatus } from '@workspaces/models/agent';
import { WorkspaceAgentService } from '@workspaces/services/agent.service';
import { WorkspaceService } from '@workspaces/services/workspace.service';
import { EditComponent } from '@workspaces/utils/edit-component';
import {
  BehaviorSubject,
  filter,
  fromEvent,
  interval,
  map,
  merge,
  Observable,
  of,
  startWith,
  Subscription,
  switchMap,
  take,
  tap
} from 'rxjs';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatCardContent } from '@angular/material/card';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';
import { workspaceAgentFeature, WorkspaceAgentFetch } from '@workspaces/store/agent-status.feature';
import { AppState } from 'src/app/state/app.state';
import { BooleanInput } from '@angular/cdk/coercion';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatChip, MatChipsModule } from '@angular/material/chips';
import { StatusIconPipe } from '../../utils/pipes/chip-icon-status.pipe';
import { AgentStatusPipe } from '@workspaces/utils/pipes/agent-status.pipe';
import { MatExpansionModule } from '@angular/material/expansion';
import { RequiredRoleDirective } from '@workspaces/utils/workspace-guard.directive';
import { WorkspaceFetch, workspaceListFeature } from '@workspaces/store/workspace-list.feature';

@Component({
  selector: 'app-persona-review',
  templateUrl: './persona-review.component.html',
  styleUrls: ['./persona-review.component.scss'],
  standalone: true,
  imports: [
    MatButtonModule,
    MatIcon,
    MatProgressSpinner,
    RequiredRoleDirective,
    MatCardContent,
    CommonModule,
    MatProgressBar,
    MatChip,
    StatusIconPipe,
    MatChipsModule,
    AgentStatusPipe,
    MatExpansionModule
  ]
})
export class PersonaReviewComponent extends EditComponent implements OnInit, OnDestroy {
  private readonly sub = new Subscription();
  public readonly refetchTrigger$ = new BehaviorSubject<boolean>(true);
  public triggerLoading = false;
  public lastUpdated: Date | null = null;
  public status$!: Observable<WorkspaceStatus>;
  public statusLoading$!: Observable<BooleanInput>;
  public isDisabledEdit = false;
  constructor(
    protected override readonly route: ActivatedRoute,
    protected readonly workspaceService: WorkspaceService,
    protected override readonly snackbar: MatSnackBar,
    protected override readonly router: Router,
    private readonly agentService: WorkspaceAgentService,
    private readonly store: Store<AppState>
  ) {
    super(route, snackbar, router);
  }
  override ngOnInit() {
    super.ngOnInit();
    this.store.dispatch(WorkspaceFetch({ workspaceId: this.workspaceId }));

    this.sub.add(
      this.store
        .select(workspaceListFeature.workspaceById(this.workspaceId))
        .pipe(map(workspace => !!workspace?.is_archived || !!workspace?.is_deleted || workspace?.state === 'FINISHED'))
        .subscribe(disabled => (this.isDisabledEdit = disabled))
    );

    this.status$ = this.store
      .select(workspaceAgentFeature.workspaceAgentData(this.workspaceId))
      .pipe(tap(() => (this.lastUpdated = new Date())));

    this.statusLoading$ = this.store.select(workspaceAgentFeature.workspaceAgentIsLoading(this.workspaceId));

    const focus$ = fromEvent(window, 'focus').pipe(map(() => true));
    const ONE_MINUTE_MS = 60000;
    const timer$ = interval(ONE_MINUTE_MS);
    //Only fetch once if archieved/deleted, otherwise once a minute or on focus
    this.sub.add(
      this.store
        .select(workspaceListFeature.workspaceById(this.workspaceId))
        .pipe(
          tap(x => console.log(x)),
          map(workspace => !!workspace?.is_archived || !!workspace?.is_deleted || workspace?.state !== 'FINISHED'),
          startWith(false),
          switchMap(isDisabled => {
            if (isDisabled) {
              return of(true).pipe(take(1));
            } else {
              return merge(timer$, focus$).pipe(startWith(true));
            }
          }),
          filter(() => !!this.workspaceId)
        )
        .subscribe(() => {
          this.store.dispatch(WorkspaceAgentFetch({ workspaceId: this.workspaceId! }));
        })
    );
  }

  public refetch() {
    this.store.dispatch(WorkspaceAgentFetch({ workspaceId: this.workspaceId! }));
  }

  public triggerAgent() {
    this.triggerLoading = true;
    this.sub.add(
      this.agentService
        .triggerAgent(this.workspaceId)
        .subscribe({
          next: () => {
            this.refetchTrigger$.next(true);
            this.store.dispatch(WorkspaceAgentFetch({ workspaceId: this.workspaceId! }));
          }
        })
        .add(() => {
          this.triggerLoading = false;
        })
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}
